import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useDispatch } from 'react-redux';

import { createStyles, Divider, ListItemIcon, MenuItem, Theme, WithStyles, withStyles } from '@material-ui/core';
import Menu, { MenuProps } from '@material-ui/core/Menu';
import {
  ArrowBackIos as ArrowBackIcon,
  CheckCircle as CheckIcon,
  Forum as ForumIcon,
  Language as LanguageIcon,
  Refresh as RefreshIcon,
} from '@material-ui/icons';
import classnames from 'classnames';
import _ from 'lodash';

import { changeLocale } from '@lib/actions';
import { useCurrentLocale } from '@lib/hooks';
import { Locale, locales } from '@lib/providers/LanguageProvider/LanguageProvider';

import { SvgIconWrapper, Typography } from 'components/v2';
import { isMobile } from 'utils/isMobile';

import { useLumaBotContext } from '../LumaBot.context';
import { ELumaBotFlowType } from '../WIP/ui-model/types';

const styles = (theme: Theme) =>
  createStyles({
    hidden: {
      visibility: 'hidden',
    },
    languageItem: {
      padding: `${theme.spacing.unit * 1.5} ${theme.spacing.unit * 3}`,
      '&$languageOption': {
        display: 'flex',
        justifyContent: 'space-between',
      },
    },
    checkIcon: {
      marginRight: 0,
    },
    languageTitleIcon: {
      position: 'absolute',
      fontSize: theme.typography.pxToRem(18),
    },
    mainMenuIcon: {
      fontSize: theme.typography.pxToRem(20),
    },
    languageTitle: {
      margin: 'auto',
    },
    languageOption: {},
    menuMaxWidth: {
      maxWidth: 'unset',
    },
    mobileView: {
      left: '0px !important',
    },
  });

const messages = defineMessages({
  restartConversation: {
    id: 'modules.LumaBot.MenuActions.restartConversation',
    defaultMessage: 'Restart Conversation',
  },
  speakToStaff: {
    id: 'modules.LumaBot.MenuActions.speakToStaff',
    defaultMessage: 'Speak with Staff',
  },
  changeLanguage: {
    id: 'modules.LumaBot.MenuActions.changeLanguage',
    defaultMessage: 'Change Language',
  },
});

const mappingLocales = {
  [Locale.EnglishUS]: 'English',
  [Locale.SpanishUS]: 'Español',
  [Locale.PortugueseBR]: 'Português',
};

export type MenuActionsAction = 'restartConversation';

export interface MenuActionsProps extends Omit<MenuProps, 'classes' | 'onClose'>, WithStyles<typeof styles> {
  onClose: () => void;
}

const MenuActions: React.FC<MenuActionsProps> = ({ classes, onClose, ...rest }) => {
  const { flowTemplates, pushFlowTemplate } = useLumaBotContext();
  const dispatch = useDispatch();
  const currentLocale = useCurrentLocale();
  const { formatMessage } = useIntl();
  const [isLanguageMenu, setLanguageMenu] = useState(false);
  const menuClasses = { paper: classnames(classes.menuMaxWidth, { [classes.mobileView]: isMobile.any() }) };

  if (isLanguageMenu) {
    const { open, ...languageMenuProps } = rest;

    return (
      <Menu
        onClose={() => {
          onClose();
          setLanguageMenu(false);
        }}
        open={isLanguageMenu}
        classes={menuClasses}
        {...languageMenuProps}
      >
        <MenuItem onClick={() => setLanguageMenu(!isLanguageMenu)} className={classes.languageItem}>
          <ListItemIcon className={classes.languageTitleIcon}>
            <SvgIconWrapper Icon={ArrowBackIcon} fontSize="inherit" />
          </ListItemIcon>
          <Typography variant="sectionHeader" className={classes.languageTitle}>
            {formatMessage(messages.changeLanguage)}
          </Typography>
        </MenuItem>
        <Divider />
        {_.map(locales, (locale) => (
          <MenuItem
            key={locale}
            selected={locale === currentLocale}
            className={classnames(classes.languageItem, classes.languageOption)}
            onClick={() => {
              dispatch(changeLocale(locale));
            }}
          >
            <Typography variant="body2">{mappingLocales[locale]}</Typography>
            <ListItemIcon
              className={classnames(
                { [classes.hidden]: locale !== currentLocale },
                { [classes.checkIcon]: locale === currentLocale },
              )}
            >
              <SvgIconWrapper Icon={CheckIcon} color="success" />
            </ListItemIcon>
          </MenuItem>
        ))}
      </Menu>
    );
  }

  return (
    <Menu
      onClose={() => {
        onClose();
        setLanguageMenu(false);
      }}
      classes={menuClasses}
      {...rest}
    >
      <MenuItem
        onClick={() => {
          pushFlowTemplate(ELumaBotFlowType.Welcome, formatMessage(messages.restartConversation));
          onClose();
        }}
      >
        <ListItemIcon className={classes.mainMenuIcon}>
          <SvgIconWrapper Icon={RefreshIcon} fontSize="inherit" />
        </ListItemIcon>
        <Typography variant="sectionHeader">{formatMessage(messages.restartConversation)}</Typography>
      </MenuItem>
      <Divider />
      {_.find(flowTemplates, { flowType: ELumaBotFlowType.SpeakWithStaff }) && (
        <>
          <MenuItem
            onClick={() => {
              pushFlowTemplate(ELumaBotFlowType.SpeakWithStaff, formatMessage(messages.speakToStaff));
              onClose();
            }}
          >
            <ListItemIcon className={classes.mainMenuIcon}>
              <SvgIconWrapper Icon={ForumIcon} fontSize="inherit" />
            </ListItemIcon>
            <Typography variant="sectionHeader">{formatMessage(messages.speakToStaff)}</Typography>
          </MenuItem>
          <Divider />
        </>
      )}
      <MenuItem onClick={() => setLanguageMenu(!isLanguageMenu)}>
        <ListItemIcon className={classes.mainMenuIcon}>
          <SvgIconWrapper Icon={LanguageIcon} fontSize="inherit" />
        </ListItemIcon>
        <Typography variant="sectionHeader">{formatMessage(messages.changeLanguage)}</Typography>
      </MenuItem>
      <Divider />
    </Menu>
  );
};

export default withStyles(styles, { name: 'MenuActions' })(MenuActions);
